

import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Button,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  createInsurance,
  updateInsurance,
  getInsuranceById,
  listInsCompanies,
  listAgents,
  listRiskItems
} from '../../api';
import img from '../../assets/banner.jpg';

const InsuranceForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!(location.state?.data || id);

  const [formData, setFormData] = useState({
    insurance_company: '',
    agent_name: '',
    agent_contact_no: '',
    policy_no: '',
    policy_date: '',
    policy_type: '',
    covered_locations: [{ covered_location: '', covered_entity: '' }],
    occupancy: '',
    risk_covers: [{
      risk_cover: '',
      sum_assured: '',
      rate: '',
      gst_value: '',
      policy_value: '',
      current_requirement: '',
      difference: ''
    }],
    total_policy_value: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState(null);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [agents, setAgents] = useState([]);
  const [riskItems, setRiskItems] = useState([]);

  useEffect(() => {
    const loadFormData = async () => {
      try {
        // Fetch data for dropdowns
        const [companies, agentsData, risks] = await Promise.all([
          listInsCompanies(), // Fetch insurance companies
          listAgents(),       // Fetch agents
          listRiskItems()     // Fetch risk items
        ]);
        setInsuranceCompanies(Array.isArray(companies) ? companies : []);
        setAgents(Array.isArray(agentsData) ? agentsData : []);
        setRiskItems(Array.isArray(risks) ? risks : []);

        // Load existing insurance data if in edit mode
        const initial = location.state?.data;
        if (initial) {
          setFormData({
            insurance_company: initial.insurance_company || '',
            agent_name: initial.agent_name || '',
            agent_contact_no: initial.agent_contact_no || '',
            policy_no: initial.policy_no || '',
            policy_date: initial.policy_date ? initial.policy_date.split('T')[0] : '',
            policy_type: initial.policy_type || '',
            covered_locations: initial.covered_locations || [{ covered_location: initial.covered_location || '', covered_entity: '' }],
            occupancy: initial.occupancy || '',
            risk_covers: initial.risk_covers || [{
              risk_cover: initial.risk_cover || '',
              sum_assured: initial.sum_assured || '',
              rate: initial.rate || '',
              gst_value: initial.gst_value || '',
              policy_value: initial.policy_value || '',
              current_requirement: initial.current_requirement || '',
              difference: initial.difference || ''
            }],
            total_policy_value: initial.total_policy_value || ''
          });
        } else if (id) {
          const fetched = await getInsuranceById(id);
          setFormData({
            insurance_company: fetched.insurance_company || '',
            agent_name: fetched.agent_name || '',
            agent_contact_no: fetched.agent_contact_no || '',
            policy_no: fetched.policy_no || '',
            policy_date: fetched.policy_date ? fetched.policy_date.split('T')[0] : '',
            policy_type: fetched.policy_type || '',
            covered_locations: fetched.covered_locations || [{ covered_location: fetched.covered_location || '', covered_entity: '' }],
            occupancy: fetched.occupancy || '',
            risk_covers: fetched.risk_covers || [{
              risk_cover: fetched.risk_cover || '',
              sum_assured: fetched.sum_assured || '',
              rate: fetched.rate || '',
              gst_value: fetched.gst_value || '',
              policy_value: fetched.policy_value || '',
              current_requirement: fetched.current_requirement || '',
              difference: fetched.difference || ''
            }],
            total_policy_value: fetched.total_policy_value || ''
          });
        }
      } catch (err) {
        setError('Failed to load insurance data, companies, agents, or risk items');
        console.error('Error loading data:', err);
      } finally {
        setLoadingData(false);
      }
    };

    loadFormData();
  }, [id, location.state]);

  const handleChange = (e, index = null, fieldType = null) => {
    const { name, value } = e.target;
    if (fieldType === 'covered_location' || fieldType === 'covered_entity') {
      const updatedLocations = [...formData.covered_locations];
      updatedLocations[index][fieldType] = value;
      setFormData({ ...formData, covered_locations: updatedLocations });
    } else if (fieldType) {
      const updatedRiskCovers = [...formData.risk_covers];
      updatedRiskCovers[index][fieldType] = value;

      // Auto-calculate Policy Value: Sum Assured + (Sum Assured * Rate / 100) + (Sum Assured * GST Value / 100)
      const sumAssured = parseFloat(updatedRiskCovers[index].sum_assured) || 0;
      const rate = parseFloat(updatedRiskCovers[index].rate) || 0;
      const gstValue = parseFloat(updatedRiskCovers[index].gst_value) || 0;
      updatedRiskCovers[index].policy_value = (sumAssured + (sumAssured * rate / 100) + (sumAssured * gstValue / 100)).toFixed(2);

      // Auto-calculate Difference
      const currentRequirement = parseFloat(updatedRiskCovers[index].current_requirement) || 0;
      updatedRiskCovers[index].difference = (currentRequirement - sumAssured).toFixed(2);

      // Auto-calculate Total Policy Value
      const totalPolicyValue = updatedRiskCovers.reduce((sum, cover) => {
        return sum + (parseFloat(cover.policy_value) || 0);
      }, 0).toFixed(2);

      setFormData({ ...formData, risk_covers: updatedRiskCovers, total_policy_value: totalPolicyValue });
    } else if (name === 'agent_name') {
      // When agent_name changes, auto-populate agent_contact_no
      const selectedAgent = agents.find(agent => agent.name === value);
      setFormData({
        ...formData,
        agent_name: value,
        agent_contact_no: selectedAgent ? selectedAgent.contact_person_phone || '' : ''
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const addCoveredLocation = () => {
    setFormData({
      ...formData,
      covered_locations: [...formData.covered_locations, { covered_location: '', covered_entity: '' }]
    });
  };

  const deleteCoveredLocation = (index) => {
    if (formData.covered_locations.length === 1) return; // Prevent deleting the last location
    const updatedLocations = formData.covered_locations.filter((_, i) => i !== index);
    setFormData({ ...formData, covered_locations: updatedLocations });
  };

  const addRiskCover = () => {
    setFormData({
      ...formData,
      risk_covers: [...formData.risk_covers, {
        risk_cover: '',
        sum_assured: '',
        rate: '',
        gst_value: '',
        policy_value: '',
        current_requirement: '',
        difference: ''
      }]
    });
  };

  const deleteRiskCover = (index) => {
    if (formData.risk_covers.length === 1) return; // Prevent deleting the last risk cover
    const updatedRiskCovers = formData.risk_covers.filter((_, i) => i !== index);
    const totalPolicyValue = updatedRiskCovers.reduce((sum, cover) => {
      return sum + (parseFloat(cover.policy_value) || 0);
    }, 0).toFixed(2);
    setFormData({ ...formData, risk_covers: updatedRiskCovers, total_policy_value: totalPolicyValue });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEdit) {
        await updateInsurance(id || formData.id, formData);
      } else {
        await createInsurance(formData);
      }
      navigate('/view-all', {
        state: { message: isEdit ? 'Insurance updated successfully!' : 'Insurance created successfully!' }
      });
    } catch (err) {
      setError(err.message || 'Submission failed');
      console.error('Error submitting insurance form:', err);
    } finally {
      setLoading(false);
    }
  };

  const isDateField = (type) => type === 'date';

  if (loadingData) return <Box textAlign="center" py={4}><CircularProgress /></Box>;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: `url(${img})`,
        backgroundSize: 'cover',
        backgroundAttachment: 'fixed',
        py: 5,
        px: 2
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 9,
          borderRadius: 3,
          backgroundColor: '#121212',
          color: 'text.primary',
          maxWidth: 1200,
          mx: 'auto',
          boxShadow: 3
        }}
      >
        <Typography variant="h6" align="center" gutterBottom>
          {isEdit ? 'Edit Insurance' : 'Insurance Renewal Details'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={4} direction="column" >
            {/* First Row: Insurance Company */}
            <Grid container item spacing={4}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small" style={{maxWidth:'1000px' ,width:'500%'}}>
                  <InputLabel id="insurance-company-label" sx={{ color: '#fafaf9ff' }}>
                    Insurance Company
                  </InputLabel>
                  <Select
                    labelId="insurance-company-label"
                    name="insurance_company"
                    value={formData.insurance_company}
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e0b256' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e0b256' },
                      color: '#fff',
                      backgroundColor: '#1e1e1e',
                    }}
                  >
                    <MenuItem value="">
                      <em>Select an insurance company</em>
                    </MenuItem>
                    {insuranceCompanies.map((company) => (
                      <MenuItem key={company._id} value={company.company_name}>
                        {company.company_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Second Row: Agent Name (Dropdown), Agent Contact No, Policy No, Policy Date, Policy Type */}
            <Grid container item spacing={4}>
              <Grid item xs={12} sm={2.4} >
                <FormControl fullWidth size="small" style={{marginRight:'185px'}}>
                  <InputLabel id="agent-name-label" sx={{ color: '#fdfcfbff' }}>
                    Agent Name
                  </InputLabel>
                  <Select
                    labelId="agent-name-label"
                    name="agent_name"
                    value={formData.agent_name}
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e0b256' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e0b256' },
                      color: '#fff',
                      backgroundColor: '#1e1e1e',
                     
                    }}
                  >
                    <MenuItem value="">
                      <em>Select an agent</em>
                    </MenuItem>
                    {agents.map((agent) => (
                      <MenuItem key={agent._id} value={agent.name}>
                        {agent.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {[
                { label: 'Agent Contact No', name: 'agent_contact_no', disabled: true },
                { label: 'Policy No', name: 'policy_no' },
                { label: 'Policy Date', name: 'policy_date', type: 'date' },
                { label: 'Policy Type', name: 'policy_type' }
              ].map((field) => (
                <Grid item xs={12} sm={2.4} key={field.name}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={field.label}
                    name={field.name}
                    type={field.type || 'text'}
                    value={formData[field.name]}
                    onChange={handleChange}
                    disabled={field.disabled || false}
                    InputLabelProps={isDateField(field.type) ? { shrink: true } : {}}
                    sx={{
                      '& label.Mui-focused': { color: '#e0b256' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc' },
                        '&:hover fieldset': { borderColor: '#e0b256' },
                        '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                      }
                    }}
                    {...(isDateField(field.type)
                      ? { inputProps: { sx: { py: 1 } } }
                      : { InputProps: { sx: { py: 1 } } })}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Third Row: Covered Location with Plus and Delete Buttons */}
            {formData.covered_locations.map((location, index) => (
              <Grid container item spacing={4} key={index}>
                <Grid item xs={12} sm={formData.covered_locations.length > 1 ? 10.5 : 11}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Covered Location"
                    name={`covered_location_${index}`}
                    value={location.covered_location}
                    onChange={(e) => handleChange(e, index, 'covered_location')}
                    sx={{
                      
                    
                      '& label.Mui-focused': { color: '#e0b256' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc' },
                        '&:hover fieldset': { borderColor: '#e0b256' },
                        '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                      }
                    }}
                    InputProps={{ sx: { py: 1 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={1}>
                  {index === 0 ? (
                    <IconButton onClick={addCoveredLocation} sx={{ color: '#e0b256' }}>
                      <AddCircleIcon />
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => deleteCoveredLocation(index)} sx={{ color: '#e0b256' }}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            ))}

            {/* Fourth Row: Covered Entity, Occupancy */}
            <Grid container item spacing={4}>
              {[
                { label: 'Covered Entity', name: 'covered_locations[0].covered_entity' },
                { label: 'Occupancy', name: 'occupancy' }
              ].map((field) => (
                <Grid item xs={12} sm={6} key={field.name}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={field.label}
                    name={field.name}
                    value={field.name === 'covered_locations[0].covered_entity' ? formData.covered_locations[0].covered_entity : formData[field.name]}
                    onChange={(e) => handleChange(e, field.name === 'covered_locations[0].covered_entity' ? 0 : null, field.name === 'covered_locations[0].covered_entity' ? 'covered_entity' : null)}
                    sx={{
                      '& label.Mui-focused': { color: '#e0b256' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#ccc' },
                        '&:hover fieldset': { borderColor: '#e0b256' },
                        '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                      }
                    }}
                    InputProps={{ sx: { py: 1 } }}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Fifth Row: Risk Cover, Sum Assured, Rate, GST Value, Policy Value, Current Requirement, Difference with Plus and Delete Buttons */}
            {formData.risk_covers.map((cover, index) => (
              <Grid container item spacing={4} key={index}>
                <Grid item xs={12} sm={1.71}>
                  <FormControl fullWidth size="small" style={{marginRight:'185px'}}>
                    <InputLabel id={`risk-cover-label-${index}`} sx={{ color: '#fdfdfcff' }}>
                      Risk Cover
                    </InputLabel>
                    <Select
                      labelId={`risk-cover-label-${index}`}
                      name={`risk_cover_${index}`}
                      value={cover.risk_cover}
                      onChange={(e) => handleChange(e, index, 'risk_cover')}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e0b256' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e0b256' },
                        color: '#fff',
                        backgroundColor: '#1e1e1e',
                      }}
                    >
                      <MenuItem value="">
                        <em>Select a risk item</em>
                      </MenuItem>
                      {riskItems.map((item) => (
                        <MenuItem key={item._id} value={item.name}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {[
                  { label: 'Sum Assured', name: 'sum_assured', type: 'number' },
                  { label: 'Rate (%)', name: 'rate', type: 'number' },
                  { label: 'GST (%)', name: 'gst_value', type: 'number' },
                  { label: 'Policy Value', name: 'policy_value', type: 'number', disabled: true },
                  { label: 'Current Requirement', name: 'current_requirement', type: 'number' },
                  { label: 'Difference', name: 'difference', type: 'number', disabled: true }
                ].map((field) => (
                  <Grid item xs={12} sm={1.71} key={field.name}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      label={field.label}
                      name={`${field.name}_${index}`}
                      type={field.type || 'text'}
                      value={cover[field.name]}
                      onChange={(e) => handleChange(e, index, field.name)}
                      disabled={field.disabled || false}
                      sx={{
                        '& label.Mui-focused': { color: '#e0b256' },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: '#ccc' },
                          '&:hover fieldset': { borderColor: '#e0b256' },
                          '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                        }
                      }}
                      InputProps={{ sx: { py: 1 } }}
                    />
                  </Grid>
                ))}
                <Grid item xs={12} sm={0.5}>
                  {index === 0 ? (
                    <IconButton onClick={addRiskCover} sx={{ color: '#e0b256' }}>
                      <AddCircleIcon />
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => deleteRiskCover(index)} sx={{ color: '#e0b256' }}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            ))}

            {/* Sixth Row: Total Policy Value */}
            <Grid container item spacing={4}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Total Policy Value"
                  name="total_policy_value"
                  type="number"
                  value={formData.total_policy_value}
                  disabled
                  sx={{
                    '& label.Mui-focused': { color: '#e0b256' },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#ccc' },
                      '&:hover fieldset': { borderColor: '#e0b256' },
                      '&.Mui-focused fieldset': { borderColor: '#e0b256' }
                    }
                  }}
                  InputProps={{ sx: { py: 1 } }}
                />
              </Grid>
            </Grid>

            {/* Submit and Cancel Buttons */}
            <Grid container item spacing={4}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{
                    py: 1.25,
                    mt: 1.5,
                    fontWeight: 'bold',
                    backgroundColor: '#e0b256',
                    color: '#000',
                    '&:hover': { backgroundColor: '#c99e3c' }
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : isEdit ? 'Update' : 'Submit'}
                </Button>
              </Grid>
              {isEdit && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{
                      py: 1.25,
                      mt: 1,
                      fontWeight: 'bold',
                      color: '#e0b256',
                      borderColor: '#e0b256',
                      '&:hover': { backgroundColor: '#222' }
                    }}
                    onClick={() => navigate('/view-all')}
                  >
                    Cancel
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default InsuranceForm;